<template>
    <PrimaryModal
        ref="editLoyerModal"
        :btnSuccessText="loyer.uuid ? 'Éditer' : 'Ajouter' "
        :disabledBtn="!checkFieldValid"
        @ok="loyer.uuid ? editLoyer() : addLoyer()"
        :width="750"
    >
        <template v-slot:title>
            <div class="flex justify-between items-center">
                <h3 class="text-2xl text-bold text-gray-900 dark:text-gray-100">{{ loyer.uuid ? 'Éditer un loyer' : 'Ajouter un loyer' }}</h3>
                <template v-if="bien">
                <div v-if="bien.auto_loyer" class="mt-4 bg-yellow-100 border-l-4 p-5 border-yellow-400 font-medium dark:bg-yellow-500/10 dark:border-yellow-500/40">
                    <h4 class="text-gray-800 dark:text-gray-100">Loyer automatique</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Attention, il existe un loyer automatique pour ce bien.</p>
                    <p v-if="bien.auto_loyer_at" class="text-sm text-indigo-600 dark:text-indigo-400">Il se déclenche tous les {{ bien.auto_loyer_at }} de chaque mois.</p>
                </div>
                <div v-else class="mt-4 bg-blue-100 border-l-4 p-5 border-blue-400 font-medium dark:bg-blue-500/10 dark:border-blue-500/40">
                    <h4 class="text-gray-800 dark:text-gray-100">Loyer automatique</h4>
                    <router-link :to="`/bien/${bien.uuid}/settings`">
                        <p class="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer">Configurer un loyer automatique</p>
                    </router-link>
                </div>
            </template>
            </div>
        </template>
        <template v-if="loaded" v-slot:content>
            <div class="md:col-span-3">
                <div class="mt-3">
                    <SwitchForm labelRight labelInline v-model='periodePartielle' title="Loyer partiel (entrée ou sortie en cours de mois)" id='switchPeriodePartielle'/>
                </div>
                <div v-if="periodePartielle" class="grid grid-cols-1 md:grid-cols-2">
                    <DatePicker
                        v-model='loyer.periode_debut'
                        class="mt-3 mr-2"
                        title="Début de la période"
                        :validations="['required']"
                        id='periodeDebut'
                    />
                    <DatePicker
                        v-model='loyer.periode_fin'
                        class="mt-3 mr-2"
                        title="Fin de la période"
                        :validations="['required']"
                        id='periodeFin'
                    />
                    <p v-if="prorataPreview" class="md:col-span-2 text-sm text-indigo-500 italic mt-1">
                        Prorata sur {{ prorataPreview.joursOccupes }} jour(s) sur {{ prorataPreview.joursDansLeMois }} :
                        {{ prorataPreview.loyer }} € de loyer + {{ prorataPreview.charges }} € de charges, reporté automatiquement ci-dessous sauf si vous les modifiez vous-même.
                    </p>
                    <p v-else-if="!bien" class="md:col-span-2 text-sm text-gray-500 italic mt-1">
                        Sélectionnez les deux dates pour calculer le prorata.
                    </p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2">
                    <div
                        class="mt-3 mr-2"
                    >
                        <label class="form-label" for="month">
                            Mois*
                        </label>
                        <div
                            class="relative"
                        >
                            <Multiselect
                                v-model='loyer.month'
                                id='month'
                                :options="monthOptions"
                                label="label"
                                track-by="label"
                                v-bind="$multiselectBind"
                            />
                        </div>
                    </div>

                    <DatePicker
                        v-model="loyer.year"
                        class="mt-3 mr-2"
                        title="Année"
                        :validations="['required']"
                        id='year'
                        minimum-type="year"
                        maximum-type="year"
                        @changeYear="(event) => loyer.year = event.toString()"
                    />
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3">
                    <div class="mt-3 mr-2">
                        <AppInput
                            :model-value='loyer.loyer'
                            @update:model-value="onLoyerInput"
                            title='Loyer'
                            id='loyer'
                            :validations="['number']"
                            @hasError="handleFormError"
                            :check-on-create="false"
                            icon="euro-sign"
                        />
                        <p v-if="loyerValeurManuelle" class="text-xs text-orange-500 italic mt-1">
                            Valeur modifiée manuellement, elle diffère du montant calculé par le prorata ({{ prorataPreview.loyer }} €).
                        </p>
                    </div>
                    <div class="mt-3 mr-2">
                        <AppInput
                            :model-value='loyer.charges'
                            @update:model-value="onChargesInput"
                            title='Charges'
                            id='charges'
                            :validations="['number']"
                            @hasError="handleFormError"
                            :check-on-create="false"
                            icon="euro-sign"
                        />
                        <p v-if="chargesValeurManuelle" class="text-xs text-orange-500 italic mt-1">
                            Valeur modifiée manuellement, elle diffère du montant calculé par le prorata ({{ prorataPreview.charges }} €).
                        </p>
                    </div>
                    <DatePicker
                        v-model='loyer.paiement_date'
                        class="mt-3 mr-2"
                        title="Date de paiement"
                        id='paiementDate'
                    />
                </div>
                <div class="grid grid-cols-2">
                    <div
                        class="mt-3 mr-2"
                    >
                        <label class="form-label mb-0" for="month">
                            Habitant(s)*
                        </label>
                        <SelectHabitantList v-model="loyer.habitants" @update="(event) => loyer.habitants = event"/>
                    </div>
                </div>
            </div>
        </template>
        <template v-else v-slot:content>
            <SpinnerLoader class="mt-5"></SpinnerLoader>
        </template>
        <template v-slot:footer v-if="!loyer.uuid">
            <div
                class="mr-2" 
            >
                <SwitchForm labelRight labelInline v-model='sendRentReceip' :title="'Envoyer la '+ (loyer.uuid ? 'nouvelle ' : '') +'quittance de loyer par email'" id='switchSendRentReceip' class="my-3"/>
            </div>
        </template>
        <template v-slot:footer v-else>
            <p class="text-sm">
                <span class="font-bold text-md text-orange-600"><font-awesome-icon :icon="['fa', 'exclamation-circle']" /> Attention</span>
                En éditant ce loyer une nouvelle quittance sera créé. Vous pouvez l'envoyer par email depuis la liste des quittances
            </p>
        </template>
    </PrimaryModal>
</template>

<script>

import { inject } from 'vue';
import AppInput from './../../../../form/AppInput';
import DatePicker from './../../../../form/DatePicker';
import SwitchForm from './../../../../form/SwitchForm';
import SelectHabitantList from './../../../../form/habitant/SelectHabitantList.vue';
import SpinnerLoader from './../../../../loader/SpinnerLoader';
import Multiselect from 'vue-multiselect'
import PrimaryModal from './../../../../composant/PrimaryModal';
import { mapGetters } from "vuex";

export default {
    components: {
        PrimaryModal,
        AppInput,
        SwitchForm,
        DatePicker,
        Multiselect,
        SpinnerLoader,
        SelectHabitantList
    },
    data() {
        return {
            loyer: {
                uuid: null,
                year: "",
                month: null,
                loyer: "",
                charges: "",
                datePaiement: null,
                periode_debut: null,
                periode_fin: null,
                habitants: [],
            },
            periodePartielle: 0,
            loyerModifieManuellement: false,
            chargesModifieManuellement: false,
            sendRentReceip: 0,
            formInvalid: [],
            loaded: false
        }
    },

    props: {
        bien: {
            type: Object,
            default: null
        }
    },

    mounted() {
        const tokenServices = inject('tokenServices')

        this.sendRentReceip = tokenServices.getUserSettingFromKey('default_send_quittance_email') === true ? 1 : 0
    },

    computed: {
        ...mapGetters({
            monthOptions: 'getMonthOptions',
            getMonth: 'getMonth'
        }),
        checkFieldValid() {
            return this.formInvalid.length === 0 &&
            this.loyer.year && this.loyer.month &&
            this.loyer.year.toString().length === 4 &&
            (!this.periodePartielle || (this.loyer.periode_debut && this.loyer.periode_fin && this.loyer.periode_fin >= this.loyer.periode_debut));
        },
        prorataPreview() {
            if (!this.periodePartielle || !this.loyer.periode_debut || !this.loyer.periode_fin || !this.bien) {
                return null;
            }

            const debut = this.loyer.periode_debut;
            const fin = this.loyer.periode_fin;

            if (fin < debut || debut.getMonth() !== fin.getMonth() || debut.getFullYear() !== fin.getFullYear()) {
                return null;
            }

            const joursDansLeMois = new Date(debut.getFullYear(), debut.getMonth() + 1, 0).getDate();
            const joursOccupes = Math.round((fin - debut) / (1000 * 60 * 60 * 24)) + 1;
            const ratio = joursOccupes / joursDansLeMois;

            return {
                joursOccupes,
                joursDansLeMois,
                loyer: this.bien.loyer !== null && this.bien.loyer !== undefined ? Math.round(this.bien.loyer * ratio * 100) / 100 : null,
                charges: this.bien.charges !== null && this.bien.charges !== undefined ? Math.round(this.bien.charges * ratio * 100) / 100 : null,
            };
        },
        loyerValeurManuelle() {
            return !!(this.loyerModifieManuellement && this.prorataPreview && this.prorataPreview.loyer !== null && parseFloat(this.loyer.loyer) !== this.prorataPreview.loyer);
        },
        chargesValeurManuelle() {
            return !!(this.chargesModifieManuellement && this.prorataPreview && this.prorataPreview.charges !== null && parseFloat(this.loyer.charges) !== this.prorataPreview.charges);
        }
    },

    methods: {
        async openModal(loyer = null) {
            let currentHabitants = []

            if (loyer && loyer.uuid) {
                const {results} = await this.$api.habitant.getHabitants(1, this.$route.params.bienUuid, {archived: 0});
                currentHabitants = results
            }

            this.$refs.editLoyerModal.openModal();
            
            this.loyer = loyer ? {...loyer} : await this.$api.bien.getNextLoyer(this.$route.params.bienUuid);

            this.loyer.month = this.getMonth(this.loyer.month)
            this.loyer.paiement_date = this.loyer.paiement_date ? new Date(this.loyer.paiement_date) : null
            this.loyer.periode_debut = this.loyer.periode_debut ? new Date(this.loyer.periode_debut) : null
            this.loyer.periode_fin = this.loyer.periode_fin ? new Date(this.loyer.periode_fin) : null
            this.periodePartielle = (this.loyer.periode_debut || this.loyer.periode_fin) ? 1 : 0
            // Un loyer déjà prorata conserve ses montants enregistrés : on ne les recalcule pas au chargement,
            // seule une nouvelle saisie de dates (ou une modification manuelle) doit les faire évoluer.
            this.loyerModifieManuellement = !!(this.loyer.periode_debut && this.loyer.periode_fin)
            this.chargesModifieManuellement = !!(this.loyer.periode_debut && this.loyer.periode_fin)
            this.loyer.habitants = this.loyer.habitants.map(h => {
                return {
                    uuid: h.uuid,
                    selected: 1,
                    data: h
                }
            })

            for (const ch of currentHabitants) {
                const exist = this.loyer.habitants.filter(h => h.uuid === ch.uuid).length > 0

                if (!exist) {
                    this.loyer.habitants.push({
                        uuid: ch.uuid,
                        selected: 0,
                        data: ch
                    })
                }
            }

            this.loaded = true
        },
        async addLoyer() {
            const bienUuid = this.$route.params.bienUuid

            if (!this.checkFieldValid || !bienUuid) {
                return;
            }

            this.$refs.editLoyerModal.activateLoader();

            await this.$api.bien.addLoyer(bienUuid, {
                year: this.loyer.year.toString(),
                month: this.loyer.month.code,
                loyer: this.loyer.loyer ? parseFloat(this.loyer.loyer) : null,
                charges: this.loyer.charges ? parseFloat(this.loyer.charges): null,
                paiement_date: this.loyer.paiement_date ? this.loyer.paiement_date.toISOString() : undefined,
                periode_debut: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_debut) : undefined,
                periode_fin: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_fin) : undefined,
                habitants: this.loyer.habitants.filter(h => h.selected).map(h => {
                    return h.uuid
                })
            }, this.sendRentReceip)
            .then((result) => {
                this.$toast({title: 'Succès', description: "Le loyer à été créée avec succès."}, this.$toastSuccess);

                return Promise.resolve(result)
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.$refs.editLoyerModal.deactivateLoader();
            this.$refs.editLoyerModal.dismissModal();
            this.$emit('handleUpdateList');
        },

        async editLoyer() {
            const bienUuid = this.$route.params.bienUuid

            if (!this.checkFieldValid || !bienUuid || !this.loyer.uuid) {
                return;
            }

            this.$refs.editLoyerModal.activateLoader();

            await this.$api.bien.updateLoyer(bienUuid, this.loyer.uuid, {
                year: this.loyer.year.toString(),
                month: this.loyer.month.code,
                loyer: this.loyer.loyer ? parseFloat(this.loyer.loyer) : null,
                charges: this.loyer.charges ? parseFloat(this.loyer.charges) : null,
                paiement_date: this.loyer.paiement_date ? this.loyer.paiement_date.toISOString() : undefined,
                periode_debut: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_debut) : undefined,
                periode_fin: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_fin) : undefined,
                habitants: this.loyer.habitants.filter(h => h.selected).map(h => {
                    return h.uuid
                })
            })
            .then((result) => {
                this.$toast({title: 'Succès', description: "Le loyer à été édité avec succès."}, this.$toastSuccess);

                return Promise.resolve(result)
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.$refs.editLoyerModal.deactivateLoader();
            this.$refs.editLoyerModal.dismissModal();
            this.$emit('handleUpdateList');
        },

        onLoyerInput(value) {
            this.loyer.loyer = value;
            this.loyerModifieManuellement = true;
        },

        onChargesInput(value) {
            this.loyer.charges = value;
            this.chargesModifieManuellement = true;
        },

        formatDateOnly(date) {
            if (!date) {
                return undefined;
            }

            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');

            return `${year}-${month}-${day}`;
        },

        handleFormError(event) {
            if (event.value && !this.formInvalid.includes(event.name)) {
                this.formInvalid.push(event.name);
            }

            if (!event.value && this.formInvalid.includes(event.name)) {
                this.formInvalid = this.formInvalid.filter(f => f !== event.name)
            }
        },
    },

    watch: {
        prorataPreview(preview) {
            if (!preview) {
                return;
            }

            if (preview.loyer !== null && !this.loyerModifieManuellement) {
                this.loyer.loyer = preview.loyer;
            }

            if (preview.charges !== null && !this.chargesModifieManuellement) {
                this.loyer.charges = preview.charges;
            }
        }
    }
};
</script>